<template>
  <div id="search-form-panel" class="search-form-panel">
    <LoadingOverlay v-if="showSearchOverlay" />
    <div class="search-form-content">
      <div class="search-page-wrapper">
        <h2 class="search-form-title"> Search for your perfect garden</h2>
        <div ref="scrollContainer" class="search-form-content" @focusin="onInputFocus">
          <div v-if="hasActiveFilters" class="search-form-clear">
            <b-button class="reset-button" variant="outline-secondary" @click="onResetFilters">Clear filters</b-button>
          </div>
          <SearchFormLocationDistance
            :distance="distance"
            :distance-options="distanceOptions"
            :disabled="!!gardenNameInput.trim()"
            @update:distance="distance = $event"
            @input="onLocationInput"
            @location-selected="onLocationSelected"
            @clear-inputs="onClearInputs"
            @use-current-location="onUseCurrentLocation"
          />
          <SearchFormSpacer />
          <SearchFormGardenName :value="gardenNameInput" :disabled="!!searchTerm || !!pendingLocation" @input="onGardenNameInput" />
          <SearchFormVisitOptions
            :visit-option="visitOption"
            :visit-options="visitOptions"
            :date-open-from="dateOpenFrom"
            :date-open-to="dateOpenTo"
            @update:visit-option="visitOption = $event"
            @update:date-range="onDateRangeUpdate"
          />
          <SearchFormOpeningType
            :opening-type-option="effectiveOpeningTypeOption"
            :opening-type-options="openingTypeOptions"
            @update:opening-type-option="openingTypeOption = $event"
          />
          <SearchFormFiltersAccordion />
          <div class="search-form-actions">
            <b-button class="search-button" variant="primary" :disabled="!gardenNameInput.trim() && !pendingLocation" @click="onSearch">
              <font-awesome-icon :icon="['far', 'search']"/>
              Search
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import mapApi from '@/api/endpoints/map'
import {
  FILTER_DAYS_OPEN_ALL_DATES,
  FILTER_OPEN_STATUS_LABEL,
  FILTER_OPEN_STATUS_NO_UPCOMING_OPENINGS,
  SEARCH_CONTEXT_LOCATION,
  SEARCH_CONTEXT_NAME,
} from '@/constants'
import SearchFormLocationDistance from './sections/SearchFormLocationDistance'
import SearchFormSpacer from './sections/SearchFormSpacer'
import SearchFormGardenName from './sections/SearchFormGardenName'
import SearchFormVisitOptions from './sections/SearchFormVisitOptions'
import SearchFormOpeningType from './sections/SearchFormOpeningType'
import SearchFormFiltersAccordion from './sections/SearchFormFiltersAccordion'
import LoadingOverlay from '@/components/LoadingOverlay'

export default {
  name: 'SearchFormPanel',

  provide: {
    silentFilters: true,
  },

  components: {
    SearchFormLocationDistance,
    SearchFormSpacer,
    SearchFormGardenName,
    SearchFormVisitOptions,
    SearchFormOpeningType,
    SearchFormFiltersAccordion,
    LoadingOverlay,
  },

  data() {
    return {
      isSubmitting: false,
      gardenNameInput: '',
      pendingLocation: null,
      // null on cold-start and after Clear filters; flips to SEARCH_CONTEXT_NAME
      // or SEARCH_CONTEXT_LOCATION on the first keystroke in each input.
      lastSearchType: null,
      customDateRangeSelected: false,
      distanceOptions: [
        { text: '1', value: 1 },
        { text: '5', value: 5 },
        { text: '10', value: 10 },
        { text: '25', value: 25 },
        { text: '50', value: 50 },
        { text: '100', value: 100 },
      ],
      distance: 10,
      geocodeTimer: null,
      // Bumped by cancelPendingGeocode() to invalidate any in-flight /geocode
      // response — see docs/search-form-async-lifecycle.md.
      geocodeEpoch: 0,
      openingTypeOption: null,
      openingTypeOptions: [
        { text: 'Standard opening', value: 'standard-opening' },
        { text: 'By arrangement visit', value: 'by-arrangement-visit' },
      ],
    }
  },

  async created() {
    // Returning from a "Your location" search via Edit-search must land on a
    // truly fresh form — that search type is marked on submit specifically so
    // we can detect this round-trip and bypass the usual filter rehydration.
    if (this.$store.state.garden.selectedSearchType.value === 'yourLocation') {
      this.onResetFilters()
      return
    }

    const gardenFilter = this.$store.getters['filter/sectionFilterSelected']('garden')
    if (gardenFilter && gardenFilter.length) {
      this.gardenNameInput = Array.isArray(gardenFilter) ? gardenFilter[0] : gardenFilter
      this.lastSearchType = SEARCH_CONTEXT_NAME
    }

    if (this.searchTerm) {
      this.lastSearchType = SEARCH_CONTEXT_LOCATION
      this.geocodeTimer = setTimeout(async () => {
        // Re-read searchTerm at fire time: child mounts (LocationSearch.mounted)
        // can call updateSearchType, which resets the store searchTerm to null.
        // The /geocode endpoint returns a broad "United Kingdom" fallback for an
        // empty query, which would silently populate pendingLocation and lock
        // both inputs (against the form's mutual-exclusivity invariant).
        const term = this.searchTerm
        if (!term || !term.trim()) return
        // Skip if the form is in a garden-name search context — geocoding the
        // garden name would set pendingLocation and lock the input the user is
        // actually trying to use.
        if (this.gardenNameInput && this.gardenNameInput.trim()) return
        const epoch = this.geocodeEpoch
        try {
          const response = await mapApi.location({ address: term })
          // Drop response if the form was reset during the await — see docs/search-form-async-lifecycle.md.
          if (epoch !== this.geocodeEpoch) return
          if (response.data.results && response.data.results.length) {
            const [firstResult] = response.data.results
            this.pendingLocation = firstResult
            this.clearSelectedLocation()
          }
        } catch (e) {
          // geocode failed, user can still search manually
        }
      }, 1000)
    }
  },

  beforeDestroy() {
    this.cancelPendingGeocode()
  },

  watch: {
    searchTerm(value) {
      if (value) {
        this.gardenNameInput = ''
      } else {
        this.pendingLocation = null
      }
    },
    loadingGardenList(newVal, oldVal) {
      // Clear the local submit flag only when a load completes (true → false).
      // Holding it through the click → dispatch → navigate window keeps the
      // overlay visible across the race between Vuex flipping the flag and
      // Vue Router unmounting this component, which otherwise caused the
      // overlay to skip the second search after Edit-search rehydrate.
      if (oldVal && !newVal) this.isSubmitting = false
    },
  },

  computed: {
    ...mapGetters('filter', [
      'dynamicFilterSectionByKey',
      'sectionFilterSelected',
      'selectedFilters',
    ]),

    ...mapGetters('garden', [
      'searchTerm',
      'gardenSearchType',
      'locationSearchType',
      'yourLocationSearchType',
      'loadingGardenList',
    ]),

    ...mapGetters('location', [
      'useCurrentLocation',
    ]),

    showSearchOverlay() {
      return this.isSubmitting || this.loadingGardenList
    },

    hasActiveFilters() {
      const hasGardenName = !!this.gardenNameInput.trim()
      const hasLocation = !!this.searchTerm
      const hasNonDefaultFilters = this.selectedFilters.some((f) => {
        if (f.filter === 'days_open' && f.options === 'next-7-days') return false
        if (f.filter === 'opening_type' && f.options === 'opening') return false
        return true
      })
      return hasGardenName || hasLocation || hasNonDefaultFilters || !!this.pendingLocation
    },

    openingTypeOptionFromStore() {
      const value = this.sectionFilterSelected('opening_type')
      const v = Array.isArray(value) ? value[0] : value
      // When no opening_type filter is in the store (e.g. after a garden-name
      // search auto-deselected it), surface that as the empty-string sentinel
      // so the radio group renders no active button. Without this, the UI
      // would silently default to `Standard opening` and clobber the
      // deselected state on the round-trip back to the home form.
      if (!v) return ''
      return v === 'by-arrangement' ? 'by-arrangement-visit' : 'standard-opening'
    },

    effectiveOpeningTypeOption() {
      return this.openingTypeOption != null ? this.openingTypeOption : this.openingTypeOptionFromStore
    },

    daysOpenSection() {
      return this.dynamicFilterSectionByKey('days_open')
    },

    visitOptions() {
      if (!this.daysOpenSection || !this.daysOpenSection.options) {
        return [
          { text: 'All Dates', value: 'all-dates' },
          { text: 'Today', value: 'today' },
          { text: 'This Weekend', value: 'this-weekend' },
          { text: 'Next 7 Days', value: 'next-7-days' },
          { text: 'Next 30 Days', value: 'next-30-days' },
          { text: 'Custom Date Range', value: 'custom-date-range' },
        ]
      }
      const apiOptions = this.daysOpenSection.options.map((opt) => ({
        text: opt.label,
        value: opt.value,
      }))
      return [
        ...apiOptions,
        { text: 'Custom Date Range', value: 'custom-date-range' },
      ]
    },

    dateOpenFrom() {
      const v = this.sectionFilterSelected('date_open_from')
      return Array.isArray(v) ? v[0] : v
    },
    dateOpenTo() {
      const v = this.sectionFilterSelected('date_open_to')
      return Array.isArray(v) ? v[0] : v
    },
    visitOption: {
      get() {
        const fromRaw = this.sectionFilterSelected('date_open_from')
        const toRaw = this.sectionFilterSelected('date_open_to')
        const from = Array.isArray(fromRaw) ? fromRaw[0] : fromRaw
        const to = Array.isArray(toRaw) ? toRaw[0] : toRaw

        if (from && to) {
          return 'custom-date-range'
        }
        if (this.customDateRangeSelected) {
          return 'custom-date-range'
        }

        const selected = this.sectionFilterSelected('days_open')
        const value = Array.isArray(selected) ? selected[0] : selected
        if (value && this.visitOptions.some((o) => o.value === value)) {
          return value
        }
        return 'all-dates'
      },
      set(value) {
        if (value === 'custom-date-range') {
          this.customDateRangeSelected = true
          return
        }
        this.customDateRangeSelected = false
        this.updateTag({
          filter: 'days_open',
          type: 'radio',
          options: value,
          initial: true,
        })
      },
    },
  },

  methods: {
    ...mapActions('filter', ['updateTag', 'resetFilters']),
    ...mapActions('garden', ['filterGardens', 'updateSearchModalShown', 'clearSearch', 'updateSearchType']),
    ...mapActions('location', ['getSelectedLocationBoundsAndUpdate', 'clearSelectedLocation', 'setUseCurrentLocation']),

    // Direct mutation — `filter/removeFilter` action is intentionally not used
    // here because it dispatches an immediate `filterGardens` re-search.
    removeFilterTag(filter) {
      this.$store.commit('filter/removeTag', { filter }, { root: true })
    },

    cancelPendingGeocode() {
      if (this.geocodeTimer) {
        clearTimeout(this.geocodeTimer)
        this.geocodeTimer = null
      }
      this.geocodeEpoch += 1
    },

    onClearInputs() {
      this.cancelPendingGeocode()
      this.gardenNameInput = ''
      this.pendingLocation = null
      this.clearSearchTerm()
    },

    clearSearchTerm() {
      if (this.searchTerm !== null) {
        this.$store.commit('garden/updateSearchTerm', null)
      }
    },

    onInputFocus(event) {
      const targetId = event.target && event.target.id
      const sectionId = targetId === 'locationSearchInput'
        ? 'search-form-location-distance'
        : targetId === 'gardenSearchInput'
          ? 'search-form-garden-name'
          : null

      if (!sectionId) {
        return
      }

      // Defer past focus + soft-keyboard animation so our scroll wins over any
      // browser-native scroll-into-view triggered by the focus event.
      setTimeout(() => {
        const container = this.$refs.scrollContainer
        if (!container) {
          return
        }

        const section = container.querySelector(`#${sectionId}`)
        if (!section) {
          return
        }

        const containerRect = container.getBoundingClientRect()
        const sectionRect = section.getBoundingClientRect()
        const target = container.scrollTop + (sectionRect.top - containerRect.top)
        container.scrollTo({ top: target, behavior: 'smooth' })
      }, 100)
    },

    onLocationSelected(location) {
      this.pendingLocation = location
      this.gardenNameInput = ''
      this.lastSearchType = SEARCH_CONTEXT_LOCATION
    },

    onGardenNameInput(value) {
      this.gardenNameInput = value
      if (value) {
        this.clearSearchTerm()
        this.pendingLocation = null
      }
      if (this.lastSearchType !== SEARCH_CONTEXT_NAME) {
        this.applyGardenNameSwitch()
      }
      this.lastSearchType = SEARCH_CONTEXT_NAME
    },

    onLocationInput() {
      // pendingLocation is stale the moment the user edits the input again.
      this.pendingLocation = null
      // Asymmetric with onGardenNameInput: typing in Location from cold start
      // inherits the app-load defaults (Next 7 Days + Standard Opening) and
      // does NOT reset; only an explicit name → location switch does.
      if (this.lastSearchType === SEARCH_CONTEXT_NAME) {
        this.applyLocationSwitch()
      }
      this.lastSearchType = SEARCH_CONTEXT_LOCATION
    },

    applyGardenNameSwitch() {
      this.customDateRangeSelected = false
      this.resetFilters()
      this.pendingLocation = null
      this.clearSelectedLocation()
      this.updateTag({
        filter: 'days_open',
        type: 'radio',
        options: FILTER_DAYS_OPEN_ALL_DATES,
        initial: true,
      })
      this.openingTypeOption = ''
      this.removeFilterTag('opening_type')
      this.updateTag({
        filter: FILTER_OPEN_STATUS_LABEL,
        type: 'toggle',
        options: FILTER_OPEN_STATUS_NO_UPCOMING_OPENINGS,
        initial: true,
      })
    },

    applyLocationSwitch() {
      this.customDateRangeSelected = false
      this.gardenNameInput = ''
      this.openingTypeOption = null
      this.resetFilters()
    },

    async onUseCurrentLocation() {
      this.isSubmitting = true
      this.lastSearchType = SEARCH_CONTEXT_LOCATION
      await this.onSearch({ skipDispatch: true })
      // Stamp the search as `yourLocation` so the next Edit-search → home
      // round-trip resets the form instead of rehydrating filters.
      // onSearch's location branch already committed locationSearchType;
      // override that with the yourLocation marker as the last write.
      this.updateSearchType(this.yourLocationSearchType)
    },

    openingTypeToFilterValue(formValue) {
      const map = {
        'standard-opening': 'opening',
        'by-arrangement-visit': 'by-arrangement',
      }
      return map[formValue] ?? 'opening'
    },

    async onSearch(options = {}) {
      this.isSubmitting = true
      const savedVisitOption = this.visitOption
      const isGardenSearch = !!(this.gardenNameInput && this.gardenNameInput.trim())
      // Exclude filters owned by the search flow itself: days_open + opening_type
      // are reapplied explicitly below; garden is set explicitly when this is a
      // garden search; geo_bounding_box is set by the location search. open_status
      // is treated as a normal user filter and carried through on either route; a
      // stale garden-name value is cleared at the name->location input switch.
      const savedTagFilters = this.selectedFilters
        .filter((f) => {
          if (['days_open', 'opening_type', 'garden', 'geo_bounding_box'].includes(f.filter)) return false
          return true
        })
        .map((f) => ({ filter: f.filter, options: Array.isArray(f.options) ? [...f.options] : f.options }))

      if (isGardenSearch) {
        await this.updateSearchType(this.gardenSearchType)
      } else {
        await this.updateSearchType(this.locationSearchType)
      }

      this.visitOption = savedVisitOption
      savedTagFilters.forEach((f) => this.updateTag({ ...f, type: 'text' }))
      const openingType = this.effectiveOpeningTypeOption
      if (openingType) {
        await this.updateTag({
          filter: 'opening_type',
          type: 'defaulted_toggle',
          options: this.openingTypeToFilterValue(openingType),
          initial: true,
        })
      } else {
        // updateSearchType reseeds opening_type='opening' on a type change; honour
        // the user's deselected state by removing it again before the request fires.
        this.removeFilterTag('opening_type')
      }
      if (isGardenSearch) {
        await this.updateTag({ filter: 'garden', type: 'text', options: this.gardenNameInput.trim() })
      }
      if (options.skipDispatch) {
        // Caller (e.g. "Your location" toggle) owns the filterGardens dispatch
        // via a separate code path; firing it here would cause a duplicate
        // request without geo_bounding_box.
        this.updateSearchModalShown(false)
        return
      }
      if (this.pendingLocation) {
        await this.getSelectedLocationBoundsAndUpdate({
          location: this.pendingLocation,
          keepFilters: true,
        })
        this.pendingLocation = null
      } else {
        this.filterGardens()
      }
      this.updateSearchModalShown(false)
    },

    onResetFilters() {
      this.cancelPendingGeocode()
      this.gardenNameInput = ''
      this.pendingLocation = null
      this.openingTypeOption = null
      this.customDateRangeSelected = false
      this.distance = 10
      this.lastSearchType = null
      if (this.useCurrentLocation) {
        this.setUseCurrentLocation(false)
      }
      this.resetFilters()
      this.clearSearch()
    },

    onDateRangeUpdate({ date_open_from: from, date_open_to: to }) {
      if (from && to) {
        this.updateTag({
          filter: 'days_open',
          type: 'date_range',
          options: { date_open_from: from, date_open_to: to },
        })
      }
    },
  },
}
</script>

<style scoped lang="scss">
#search-form-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;

  @include media-breakpoint-up(xl) {
    min-width: 660px;
  }

  .search-form-content {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
  }

  .search-form-title {
    color: #000;
    font-size: 56px;

    @include media-breakpoint-down(lg) {
      padding: 12px 12px 0 12px;
      margin-bottom: 0;
    }
  }

  .search-form-clear {
    position: absolute;
    right: 0;

    @include media-breakpoint-down(xl) {
      right: 15px;
    }

    .reset-button {
      font-size: 16px;
      font-weight: 600;
      margin: 7px 16px;
    }
  }

  .search-page-wrapper {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background-color: #fff;

    @include media-breakpoint-up(lg) {
      background-color: $color-yellow-lighter;
      padding: 40px;
    }

    @include media-breakpoint-down(md) {
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    .search-form-content {
      flex: 1 1 0;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: scroll;
      -webkit-overflow-scrolling: touch;
      padding: 15px 0 15px 15px;
      background-color: #fff;

        @include media-breakpoint-down(xl) {
          padding: 15px;
        }

        @include media-breakpoint-down(md) {
          flex: 0 0 auto;
          min-height: auto;
          overflow: visible;
        }
    }
  }

  .search-form-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;

    @include media-breakpoint-down(md) {
      margin-bottom: 50px;
    }

    .search-button {
      font-size: 20px;
      font-weight: 600;
      padding: 19px 12px;
    }
  }
}
</style>
